// Central brygga mot native-appen. Wrappern exponerar `window.AppWrapper` när
// webben körs inne i appen; i vanliga webbläsare finns den aldrig. Varje
// hjälpfunktion här ska alltid falla tillbaka tyst (null/false) så att den
// vanliga webbupplevelsen kan ta över.

export type AppWrapperDeviceInfo = {
  model?: string;
  system?: string;
  systemVersion?: string;
  language?: string;
  appVersion?: string;
};

export type NativePhoto = { base64: string; mimeType?: string; name?: string };
export type NativeVideo = { base64: string; mimeType?: string; name?: string };
export type NativePosition = { latitude: number; longitude: number; accuracy?: number };
export type NativeSharePayload = { title?: string; text?: string; url?: string };
export type NativeBiometricResult = { success: boolean; method?: string; error?: string };
export type NativeNotificationPermission = { granted: boolean };
export type NativeNetworkStatus = { online: boolean; type?: string };
export type NativeStoreProduct = {
  id: string;
  title?: string;
  description?: string;
  price?: number;
  priceFormatted?: string;
};
export type NativePurchaseResult = {
  success: boolean;
  productId?: string;
  transactionId?: string;
  error?: string;
};

type AppWrapperApi = {
  device?: { info?: () => Promise<AppWrapperDeviceInfo> };
  location?: { get?: () => Promise<NativePosition> };
  camera?: {
    takePhoto?: (opts?: Record<string, unknown>) => Promise<NativePhoto>;
    recordVideo?: (opts?: Record<string, unknown>) => Promise<NativeVideo>;
  };
  photos?: {
    pick?: (opts?: Record<string, unknown>) => Promise<NativePhoto | NativePhoto[] | { photos?: NativePhoto[] }>;
  };
  biometrics?: {
    authenticate?: (reason: string) => Promise<NativeBiometricResult | { success?: boolean; method?: string; error?: string }>;
  };
  notifications?: {
    requestPermission?: () => Promise<NativeNotificationPermission | { granted?: boolean }>;
    setBadge?: (count: number) => Promise<unknown>;
    schedule?: (opts: Record<string, unknown>) => Promise<unknown>;
  };
  share?: (opts: NativeSharePayload) => Promise<unknown>;
  clipboard?: {
    write?: (text: string) => Promise<unknown>;
    read?: () => Promise<{ text?: string } | string>;
  };
  openUrl?: (url: string) => Promise<unknown>;
  openSettings?: () => Promise<unknown>;
  network?: { status?: () => Promise<NativeNetworkStatus> };
  screen?: {
    keepAwake?: (on: boolean) => Promise<unknown>;
    setBrightness?: (value: number) => Promise<unknown>;
    getBrightness?: () => Promise<{ value?: number }>;
  };
  flashlight?: (on: boolean) => Promise<unknown>;
  payments?: {
    getProducts?: (ids: string[]) => Promise<NativeStoreProduct[] | { products?: NativeStoreProduct[] }>;
    purchase?: (productId: string) => Promise<NativePurchaseResult>;
    restore?: () => Promise<{ restored?: string[] }>;
  };
  haptic?: (style: string) => Promise<unknown>;
  on?: (event: string, cb: (payload: unknown) => void) => void;
  off?: (event: string, cb: (payload: unknown) => void) => void;
};

function api(): AppWrapperApi | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { AppWrapper?: AppWrapperApi };
  return w.AppWrapper ?? null;
}

/** Sant bara inne i native-wrappern. */
export function isAppMode(): boolean {
  return api() !== null;
}

export type NativeLogEntry = { at: number; feature: string; ok: boolean; reason?: string };

const nativeLog: NativeLogEntry[] = [];
const nativeLogListeners = new Set<(entries: NativeLogEntry[]) => void>();

/** De senaste native-anropen, nyast först. Används av debugpanelen. */
export function getNativeLog(): NativeLogEntry[] {
  return [...nativeLog].reverse();
}

/** Prenumererar på native-loggen. Returnerar en avregistrering. */
export function subscribeNativeLog(cb: (entries: NativeLogEntry[]) => void): () => void {
  nativeLogListeners.add(cb);
  return () => nativeLogListeners.delete(cb);
}

function reportNative(feature: string, ok: boolean, reason?: string) {
  if (typeof window === "undefined") return;
  nativeLog.push({ at: Date.now(), feature, ok, ...(reason ? { reason: reason.slice(0, 180) } : {}) });
  if (nativeLog.length > 60) nativeLog.splice(0, nativeLog.length - 60);
  const snapshot = getNativeLog();
  for (const cb of nativeLogListeners) {
    try {
      cb(snapshot);
    } catch {
      /* en trasig lyssnare får inte stoppa loggningen */
    }
  }
  void import("@/lib/analytics-client")
    .then((m) =>
      m.track(ok ? "app_native_ok" : "app_native_fallback", {
        // label används av adminstatistiken för att gruppera per funktion.
        label: feature,
        meta: { feature, ...(reason ? { reason: reason.slice(0, 180) } : {}) },
      }),
    )
    .catch(() => undefined);
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err ?? "okänt fel");
}

let cachedDeviceInfo: AppWrapperDeviceInfo | null = null;

/** Hämtar och minns enhetsinfo från appen (null i webbläsare). */
export async function primeDeviceInfo(): Promise<AppWrapperDeviceInfo | null> {
  if (cachedDeviceInfo) return cachedDeviceInfo;
  const infoFn = api()?.device?.info;
  if (!infoFn) return null;
  try {
    cachedDeviceInfo = (await infoFn()) ?? null;
  } catch {
    cachedDeviceInfo = null;
  }
  if (cachedDeviceInfo && typeof window !== "undefined") {
    // Analytics läser detta direkt från window för att undvika cirkulär import.
    (window as unknown as { __appWrapperInfo?: Record<string, string> }).__appWrapperInfo = {
      ...(cachedDeviceInfo.appVersion ? { appVersion: String(cachedDeviceInfo.appVersion) } : {}),
      ...(cachedDeviceInfo.model ? { model: String(cachedDeviceInfo.model) } : {}),
      ...(cachedDeviceInfo.systemVersion
        ? { system: String(cachedDeviceInfo.systemVersion) }
        : cachedDeviceInfo.system
          ? { system: String(cachedDeviceInfo.system) }
          : {}),
    };
  }
  return cachedDeviceInfo;
}

/** Extra fält som ska finnas med på all analytics när vi är i appen. */
export async function appAnalyticsContext(): Promise<Record<string, string>> {
  if (!isAppMode()) return {};
  const info = await primeDeviceInfo();
  if (!info) return { appMode: "native" };
  const out: Record<string, string> = { appMode: "native" };
  if (info.appVersion) out.appVersion = String(info.appVersion).slice(0, 40);
  if (info.model) out.deviceModel = String(info.model).slice(0, 80);
  if (info.systemVersion) out.osVersion = String(info.systemVersion).slice(0, 40);
  return out;
}

/** Tar ett foto med telefonens kamera. Null i webbläsare eller om det misslyckas. */
export async function takeNativePhoto(): Promise<File | null> {
  const take = api()?.camera?.takePhoto;
  if (!take) return null;
  try {
    const shot = await take({ quality: 0.9 });
    const base64 = typeof shot?.base64 === "string" ? shot.base64 : "";
    if (!base64) throw new Error("tom bild");
    const file = base64ToFile(base64, shot?.mimeType ?? "image/jpeg", `foto-${Date.now()}.jpg`);
    reportNative("camera", true);
    return file;
  } catch (err) {
    reportNative("camera", false, errorText(err));
    return null;
  }
}

function asPhotoList(
  result: NativePhoto | NativePhoto[] | { photos?: NativePhoto[] } | null | undefined,
): NativePhoto[] {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (typeof result === "object" && "photos" in result && Array.isArray(result.photos)) {
    return result.photos;
  }
  return [result as NativePhoto];
}

/** Väljer bilder ur telefonens bildbibliotek. Null om appen/webbfallback ska användas. */
export async function pickNativePhotos(opts?: {
  multiple?: boolean;
  maxImages?: number;
}): Promise<File[] | null> {
  const pick = api()?.photos?.pick;
  if (!pick) return null;
  try {
    const picked = asPhotoList(
      await pick({
        multiple: opts?.multiple ?? false,
        ...(opts?.maxImages ? { maxImages: opts.maxImages } : {}),
      }),
    );
    const files = picked
      .map((photo, index) => {
        const base64 = typeof photo?.base64 === "string" ? photo.base64 : "";
        if (!base64) return null;
        const mimeType = photo?.mimeType ?? "image/jpeg";
        return base64ToFile(base64, mimeType, photo?.name ?? `bild-${Date.now()}-${index + 1}.jpg`);
      })
      .filter((file): file is File => file !== null);
    if (!files.length) throw new Error("inga bilder");
    reportNative("photos", true);
    return files;
  } catch (err) {
    reportNative("photos", false, errorText(err));
    return null;
  }
}

/** Väljer en bild ur telefonens bildbibliotek. */
export async function pickNativePhoto(): Promise<File | null> {
  const files = await pickNativePhotos({ multiple: false });
  return files?.[0] ?? null;
}

/** Spelar in video med telefonens kamera. Null om webbens filväljare ska användas. */
export async function recordNativeVideo(): Promise<File | null> {
  const record = api()?.camera?.recordVideo;
  if (!record) return null;
  try {
    // Var uttrycklig med mediatypen – annars öppnar vissa wrappers/simulatorn
    // stillbildsläget och skickar tillbaka ett foto.
    const clip = await record({
      maxDurationSeconds: 60,
      mediaType: "video",
      mediaTypes: ["video"],
      videoQuality: "high",
    });
    const base64 = typeof clip?.base64 === "string" ? clip.base64 : "";
    if (!base64) throw new Error("tom film");
    const mimeType = clip?.mimeType ?? "";
    const name = clip?.name ?? "";
    const looksLikeImage =
      /^image\//i.test(mimeType) || /\.(jpe?g|png|heic|heif|gif|webp)$/i.test(name);
    if (looksLikeImage) throw new Error("kameran gav en stillbild i stället för film");
    const file = base64ToFile(base64, mimeType || "video/mp4", name || `film-${Date.now()}.mp4`);
    reportNative("video", true);
    return file;
  } catch (err) {
    reportNative("video", false, errorText(err));
    return null;
  }
}

/** Hämtar GPS-position via appen. Null om webbens geolocation ska användas. */
export async function getNativePosition(): Promise<NativePosition | null> {
  const get = api()?.location?.get;
  if (!get) return null;
  try {
    const pos = await get();
    if (!Number.isFinite(pos?.latitude) || !Number.isFinite(pos?.longitude)) {
      throw new Error("saknar koordinater");
    }
    reportNative("location", true);
    return { latitude: pos.latitude, longitude: pos.longitude, accuracy: pos.accuracy };
  } catch (err) {
    reportNative("location", false, errorText(err));
    return null;
  }
}

/** Haptisk feedback (tyst no-op i webbläsare). */
export function hapticSignal(style: "success" | "error" | "light" | "medium" | "heavy" = "success") {
  const haptic = api()?.haptic;
  if (!haptic) return;
  haptic(style)
    .then(() => reportNative("haptic", true))
    .catch((err) => reportNative("haptic", false, errorText(err)));
}

/** Öppnar appens eget delningsark. False betyder: använd webbens delning. */
export async function shareNative(payload: NativeSharePayload): Promise<boolean> {
  const share = api()?.share;
  if (!share || (!payload.url && !payload.text)) return false;
  try {
    await share({ title: payload.title, text: payload.text, url: payload.url });
    reportNative("share", true);
    return true;
  } catch (err) {
    // Avbrutet delningsark är inget fel – men det finns heller ingen webbmeny att visa.
    const message = errorText(err);
    if (/abort|cancel|avbr/i.test(message)) {
      reportNative("share", true);
      return true;
    }
    reportNative("share", false, message);
    return false;
  }
}

/** Skriver till appens urklipp. False betyder: använd webbens clipboard. */
export async function writeNativeClipboard(text: string): Promise<boolean> {
  const write = api()?.clipboard?.write;
  if (!write || !text) return false;
  try {
    await write(text);
    reportNative("clipboard", true);
    return true;
  } catch (err) {
    reportNative("clipboard", false, errorText(err));
    return false;
  }
}

/** Läser appens urklipp. Null betyder: använd webbens clipboard. */
export async function readNativeClipboard(): Promise<string | null> {
  const read = api()?.clipboard?.read;
  if (!read) return null;
  try {
    const result = await read();
    const text = typeof result === "string" ? result : (result?.text ?? "");
    reportNative("clipboard", true);
    return text;
  } catch (err) {
    reportNative("clipboard", false, errorText(err));
    return null;
  }
}

/** Öppnar tel:, mailto: och externa länkar i appens webbläsare/program. */
export async function openNativeUrl(url: string): Promise<boolean> {
  const open = api()?.openUrl;
  if (!open || !url) return false;
  try {
    await open(url);
    reportNative("open_url", true);
    return true;
  } catch (err) {
    reportNative("open_url", false, errorText(err));
    return false;
  }
}

/** Öppnar appens systeminställningar. */
export async function openNativeSettings(): Promise<boolean> {
  const open = api()?.openSettings;
  if (!open) return false;
  try {
    await open();
    reportNative("settings", true);
    return true;
  } catch (err) {
    reportNative("settings", false, errorText(err));
    return false;
  }
}

/** Ber appen om notistillstånd. Null betyder att webbens permission ska användas. */
export async function requestNativeNotificationPermission(): Promise<boolean | null> {
  const request = api()?.notifications?.requestPermission;
  if (!request) return null;
  try {
    const result = await request();
    const granted = Boolean(result?.granted);
    reportNative("notifications", granted, granted ? undefined : "nekades");
    return granted;
  } catch (err) {
    reportNative("notifications", false, errorText(err));
    return null;
  }
}

/** Uppdaterar app-ikonens badge (0 tömmer). */
export async function setNativeBadge(count: number): Promise<boolean> {
  const setBadge = api()?.notifications?.setBadge;
  if (!setBadge) return false;
  try {
    await setBadge(Math.max(0, Math.floor(count)));
    reportNative("badge", true);
    return true;
  } catch (err) {
    reportNative("badge", false, errorText(err));
    return false;
  }
}

/** Face ID/Touch ID. Null betyder att biometri saknas och vanlig kontroll ska gälla. */
export async function authenticateNative(reason: string): Promise<NativeBiometricResult | null> {
  const authenticate = api()?.biometrics?.authenticate;
  if (!authenticate) return null;
  try {
    const result = await authenticate(reason);
    const success = Boolean(result?.success);
    const normalized: NativeBiometricResult = {
      success,
      method: typeof result?.method === "string" ? result.method : undefined,
      error: typeof result?.error === "string" ? result.error : undefined,
    };
    reportNative("biometrics", success, normalized.error);
    return normalized;
  } catch (err) {
    const message = errorText(err);
    reportNative("biometrics", false, message);
    return { success: false, error: message };
  }
}

let keepAwakeHolds = 0;
let keepAwakeApplied = false;

async function applyKeepAwake(on: boolean) {
  const keepAwake = api()?.screen?.keepAwake;
  if (!keepAwake) return;
  try {
    await keepAwake(on);
    reportNative("keep_awake", true);
  } catch (err) {
    reportNative("keep_awake", false, errorText(err));
  }
}

/**
 * Håller skärmen vaken medan ett tungt flöde pågår. Flera samtidiga flöden
 * delar samma native-läge; skärmen släpps först när alla lämnat tillbaka sin release.
 */
export function retainNativeKeepAwake(): () => void {
  if (!isAppMode()) return () => undefined;
  keepAwakeHolds += 1;
  if (!keepAwakeApplied) {
    keepAwakeApplied = true;
    void applyKeepAwake(true);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    keepAwakeHolds = Math.max(0, keepAwakeHolds - 1);
    if (keepAwakeHolds === 0 && keepAwakeApplied) {
      keepAwakeApplied = false;
      void applyKeepAwake(false);
    }
  };
}

/** Nätverksstatus från appen, om den finns. */
export async function getNativeNetworkStatus(): Promise<NativeNetworkStatus | null> {
  const status = api()?.network?.status;
  if (!status) return null;
  try {
    const result = await status();
    if (typeof result?.online !== "boolean") throw new Error("saknar status");
    reportNative("network", true);
    return result;
  } catch (err) {
    reportNative("network", false, errorText(err));
    return null;
  }
}

function base64ToFile(base64: string, mimeType: string, fallbackName: string): File {
  const safeType = mimeType || "application/octet-stream";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const ext = safeType.split("/")[1]?.split(";")[0] || "bin";
  const name = fallbackName.includes(".") ? fallbackName : `${fallbackName}.${ext}`;
  return new File([bytes], name, { type: safeType });
}

/**
 * StoreKit-köp via wrappern. Är medvetet inaktiverat tills produkterna är
 * registrerade i App Store Connect och servern verifierar kvittot.
 */
export const appPurchase = {
  available() {
    const payments = api()?.payments;
    return Boolean(payments?.getProducts && payments.purchase);
  },
  async products(ids: string[]): Promise<NativeStoreProduct[] | null> {
    const getProducts = api()?.payments?.getProducts;
    if (!getProducts || !ids.length) return null;
    try {
      const result = await getProducts(ids);
      const products = Array.isArray(result) ? result : (result?.products ?? []);
      reportNative("payments", true);
      return products;
    } catch (err) {
      reportNative("payments", false, errorText(err));
      return null;
    }
  },
  async purchase(productId: string): Promise<NativePurchaseResult | null> {
    const purchase = api()?.payments?.purchase;
    if (!purchase || !productId) return null;
    try {
      const result = await purchase(productId);
      const normalized: NativePurchaseResult = {
        success: Boolean(result?.success),
        productId: result?.productId ?? productId,
        transactionId: result?.transactionId,
        error: result?.error,
      };
      reportNative("payments", normalized.success, normalized.error);
      return normalized;
    } catch (err) {
      const message = errorText(err);
      reportNative("payments", false, message);
      return { success: false, productId, error: message };
    }
  },
  async restore(): Promise<string[] | null> {
    const restore = api()?.payments?.restore;
    if (!restore) return null;
    try {
      const result = await restore();
      reportNative("payments", true);
      return result?.restored ?? [];
    } catch (err) {
      reportNative("payments", false, errorText(err));
      return null;
    }
  },
};

/** Gissar plattform utifrån enhetsinfo eller user agent. */
export function getNativePlatform(): "ios" | "android" | null {
  if (typeof window === "undefined") return null;
  const info = cachedDeviceInfo;
  if (info?.system) {
    const s = info.system.toLowerCase();
    if (s.includes("ios") || s.includes("iphone") || s.includes("ipad")) return "ios";
    if (s.includes("android")) return "android";
  }
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return null;
}

/**
 * Öppnar en webbhandoff-länk i systemets webbläsare (Safari/Chrome).
 * Används när appen vill skicka användaren till webbsidan redan inloggad.
 */
export async function openWebHandoffUrl(url: string): Promise<boolean> {
  return openNativeUrl(url);
}
